<template>
  <b-form-group
    label-cols-sm="12"
    label-cols-lg="2"
    content-cols-lg="7"
    label-align-lg="right"
    label="Kategorija"
    label-class="ln-subtitle-text ln-darkblue noselect"
    class="category-form-group"
  >
    <treeselect
      id="search-categories"
      ref="treeselect"
      aria-label="Kategorija"
      v-model="selectedValues"
      :options="treeOptions"
      :multiple="true"
      :clearable="true"
      :searchable="true"
      :default-expand-level="1"
      placeholder="Ieškoti..."
      no-options-text="Sąrašas tuščias"
      no-results-text="Nerasta rezultatų"
      no-children-text="Nėra subkategorijų"
      :max-height="300"
      class="ln-treeselect"
      :limit-text="count => `+ dar ${count}`"
      @keyup.native.enter.stop
      @keydown.native.enter.stop
      @keydown.native.capture="onCategoryKeydownCapture"
      @open="onCategoryMenuOpen"
    >
      <template #value-label="{ node }">
        <span :class="{ 'ln-cat-chip-label': isCategoryNode(node) }">{{ node.label }}</span>
      </template>
    </treeselect>

    <small class="mt-1 form-text">
      Galite pasirinkti kategorijas arba subkategorijas
    </small>
  </b-form-group>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

export default {
  name: 'CategoryMultiselect',
  components: { Treeselect },
  props: {
    kategorijos: { type: Array, default: () => [] },
    subkategorijos: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] }
  },
  computed: {
    treeOptions() {
      if (!Array.isArray(this.categories) || this.categories.length === 0) return [];
      return this.categories.map(kat => {
        const subs = kat.subcategories || kat.subkategorijos || [];
        const node = {
          id: 'cat_' + kat.id,
          label: kat.name || kat.pavadinimas
        };
        if (subs.length > 0) {
          node.children = subs.map(sub => ({
            id: 'sub_' + sub.id,
            label: sub.name || sub.pavadinimas
          }));
        }
        return node;
      });
    },
    selectedValues: {
      get() {
        const ids = [];
        this.kategorijos.forEach(catId => ids.push('cat_' + catId));
        this.subkategorijos.forEach(subId => ids.push('sub_' + subId));
        return ids;
      },
      set(newIds) {
        const catIds = [];
        const subIds = [];
        (newIds || []).forEach(id => {
          if (typeof id !== 'string') return;
          if (id.startsWith('cat_')) {
            catIds.push(this.parseId(id.substring(4)));
          } else if (id.startsWith('sub_')) {
            subIds.push(this.parseId(id.substring(4)));
          }
        });

        this.$emit('update:kategorijos', catIds);
        this.$emit('update:subkategorijos', subIds);
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      const input = this.$el.querySelector('.vue-treeselect__input');
      if (input) input.setAttribute('aria-label', 'Kategorija');
    });
  },
  methods: {
    parseId(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n : value;
    },
    isCategoryNode(node) {
      return node && typeof node.id === 'string' && node.id.startsWith('cat_');
    },
    onCategoryKeydownCapture(evt) {
      if (evt.key === 'Tab') {
        evt.stopPropagation();
        return;
      }
      if (evt.key === ' ') {
        evt.preventDefault();
        evt.stopPropagation();
        const ts = this.$refs.treeselect;
        if (!ts || ts.menu.current == null) return;
        const node = ts.getNode(ts.menu.current);
        if (node) ts.select(node);
      }
    },
    onCategoryMenuOpen() {
      const ts = this.$refs.treeselect;
      const firstId = this.selectedValues[0];
      if (!ts || !firstId) return;
      const node = ts.getNode(firstId);
      if (node) ts.setCurrentHighlightedOption(node, true);
    }
  }
};
</script>

<style scoped>
.category-form-group small.form-text {
  color: #080b58 !important;
  opacity: 1 !important;
}
</style>

<style>
/* Global overrides (not scoped) so they reach vue-treeselect's
   menu DOM that is rendered outside the component's scoped CSS scope. */

/* Control (closed state) — match ln-border style */
.ln-treeselect .vue-treeselect__control {
  border: 1px solid #6c757d;
  border-radius: 4px;
  min-height: calc(1.5em + 0.75rem + 2px);
  background: #fff;
}

/* Match Bootstrap form-control vertical centering for placeholder + input */
.ln-treeselect .vue-treeselect__placeholder,
.ln-treeselect .vue-treeselect__input {
  padding: 0.375rem 0 0.375rem 4px;
  line-height: 1.5;
}
.ln-treeselect .vue-treeselect__multi-value {
  padding-left: 4px;
  align-items: center;
}

/* Override global `input:focus` rule so the inner search input doesn't
   render its own blue border on top of the outer control's focus halo. */
.ln-treeselect .vue-treeselect__input,
.ln-treeselect .vue-treeselect__input:focus {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  background: transparent;
}

.ln-treeselect.vue-treeselect--focused:not(.vue-treeselect--disabled) .vue-treeselect__control {
  border: 2px solid rgb(0, 92, 191);
  box-shadow: none;
}

.ln-treeselect.vue-treeselect--open .vue-treeselect__control {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-color: transparent;
}

/* Placeholder */
.ln-treeselect .vue-treeselect__placeholder {
  color: #495057;
  line-height: 26px;
}

/* Hide "Ieškoti..." placeholder when the field is focused — let the
   blinking cursor speak for itself. The visible "Kategorija" label
   still satisfies WCAG 3.3.2 since it labels the control. */
.ln-treeselect.vue-treeselect--focused .vue-treeselect__placeholder {
  display: none;
}

/* "Nerasta rezultatų" tip — match placeholder style (no warning icon) */
.ln-treeselect .vue-treeselect__tip {
  color: #495057;
}
.ln-treeselect .vue-treeselect__tip-text {
  font-size: 16px;
}
.ln-treeselect .vue-treeselect__tip .vue-treeselect__icon-container {
  display: none;
}

/* Selected tags — match original chip style */
.ln-treeselect .vue-treeselect__multi-value-item {
  background: #295abd;
  color: #fff;
  border-radius: 4px;
  padding: 2px 4px 2px 10px;
  font-size: 14px;
  line-height: 1.5;
  margin: 2px;
}

.ln-treeselect .vue-treeselect__multi-value-label {
  color: #fff;
  padding-right: 4px;
}

/* Bold the chip label when the selected node is a category (id starts with "cat_") */
.ln-treeselect .ln-cat-chip-label {
  font-weight: 700;
}

.ln-treeselect .vue-treeselect__value-remove {
  color: #fff;
  border-color: transparent;
}

.ln-treeselect .vue-treeselect__value-remove:hover {
  color: #fff;
  background: #4a7be0;
  border-radius: 2px;
}

.ln-treeselect .vue-treeselect__value-remove svg {
  color: #fff;
}

/* Dropdown menu */
.ln-treeselect .vue-treeselect__menu {
  border: 1px solid #6c757d;
  border-top: none;
  border-radius: 0 0 4px 4px;
  background: #fff;
  box-shadow: none;
}

/* Category nodes (have children) — UPPERCASE bold */
.ln-treeselect .vue-treeselect__option:not(.vue-treeselect__option--leaf) > .vue-treeselect__label-container .vue-treeselect__label {
  text-transform: uppercase;
  font-weight: 700;
  font-size: 16px;
  color: #495057;
  font-family: "Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* Subcategory leaves — UPPERCASE normal, indented */
.ln-treeselect .vue-treeselect__option--leaf .vue-treeselect__label {
  text-transform: uppercase;
  font-weight: 400;
  font-size: 16px;
  color: #495057;
  font-family: "Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* Hover / keyboard highlight */
.ln-treeselect .vue-treeselect__option--highlight {
  background: rgb(0, 92, 191);
  color: #fff;
}
.ln-treeselect .vue-treeselect__option--highlight .vue-treeselect__label {
  color: #fff !important;
}

/* Selected (already-chosen) option in the dropdown */
.ln-treeselect .vue-treeselect__option--selected {
  background: rgba(0, 92, 191, 0.85);
  color: #fff;
}
.ln-treeselect .vue-treeselect__option--selected .vue-treeselect__label {
  color: #fff !important;
  font-weight: 700;
}
.ln-treeselect .vue-treeselect__option--selected.vue-treeselect__option--highlight {
  background: rgb(0, 92, 191);
}

.ln-treeselect .vue-treeselect__option--selected .vue-treeselect__check-mark {
  color: #fff;
}

.ln-treeselect .vue-treeselect__checkbox--unchecked {
  border-color: #6c757d;
}

/* "+ dar N" pill in collapsed multi-value */
.ln-treeselect .vue-treeselect__limit-tip-text {
  color: #080b58;
}

/* Dropdown arrow on the right of the control */
.ln-treeselect .vue-treeselect__control-arrow {
  color: #495057;
}
.ln-treeselect.vue-treeselect--single .vue-treeselect__control-arrow-container,
.ln-treeselect.vue-treeselect--multi .vue-treeselect__control-arrow-container {
  width: 20px;
}
</style>
